import { useState } from 'react';
import {
  getAllItemTypes,
  getServiceRequestTags,
  getIssueTypes,
  fetchServicesProviderAndPropertyDeveloperList,
} from 'src/services/client';
import {
  AssetType,
  ServiceProvider,
  ServiceRequestTag,
  IssueType,
} from 'src/pages/Reporting/CustomHooks/types';

const useFetchServiceRequestStatsFormData = () => {
  const [assetTypes, setAssetTypes] = useState<AssetType[] | null>(null);
  const [loadingAssetTypes, setLoadingAssetTypes] = useState<boolean>(false);

  const [serviceRequestTags, setServiceRequestTags] = useState<
    ServiceRequestTag[] | null
  >(null);
  const [loadingTags, setLoadingTags] = useState<boolean>(false);

  const [issueTypes, setIssueTypes] = useState<IssueType[] | null>(null);
  const [loadingIssueTypes, setLoadingIssueTypes] = useState<boolean>(false);

  const [serviceProviders, setServiceProviders] = useState<
    ServiceProvider[] | null
  >(null);
  const [loadingServiceProviders, setLoadingServiceProviders] =
    useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  const fetchAssetTypes = async () => {
    setLoadingAssetTypes(true);
    try {
      const apiResult: any = await getAllItemTypes();

      const assetData: AssetType[] = await apiResult.json();
      setAssetTypes(assetData || []);
    } catch (error) {
      console.error('Asset types fetch error:', error);
      setError('Failed to load asset types');
    } finally {
      setLoadingAssetTypes(false);
    }
  };

  const fetchTags = async () => {
    setLoadingTags(true);
    try {
      const apiResult: any = await getServiceRequestTags();

      const tagsData: ServiceRequestTag[] = await apiResult.json();
      setServiceRequestTags(tagsData || []);
    } catch (error) {
      console.error('Service request tags fetch error:', error);
      setError('Failed to load service request tags');
    } finally {
      setLoadingTags(false);
    }
  };

  const fetchIssueTypes = async () => {
    setLoadingIssueTypes(true);
    try {
      const apiResult: any = await getIssueTypes();

      const issueTypesData: IssueType[] = await apiResult.json();
      setIssueTypes(issueTypesData || []);
    } catch (error) {
      console.error('Issue types fetch error:', error);
      setError('Failed to load issue types');
    } finally {
      setLoadingIssueTypes(false);
    }
  };

  const fetchServiceProviders = async (userId: string, userRole: string) => {
    setLoadingServiceProviders(true);
    try {
      const apiResult: any =
        await fetchServicesProviderAndPropertyDeveloperList(userId, userRole);

      const serviceProvidersData: ServiceProvider[] = await apiResult.json();
      setServiceProviders(serviceProvidersData || []);
    } catch (error) {
      console.error('Service providers fetch error:', error);
      setError('Failed to load service providers');
    } finally {
      setLoadingServiceProviders(false);
    }
  };

  const fetchData = async (userId: string, userRole: string) => {
    setError(null);

    await Promise.all([
      fetchAssetTypes(),
      fetchTags(),
      fetchIssueTypes(),
      fetchServiceProviders(userId, userRole),
    ]);
  };

  const getAssetTypeOptions = () => {
    if (!assetTypes) return [];
    return assetTypes
      .filter((type: AssetType) => !type.isDeleted)
      .map((type: AssetType) => ({
        value: type.name,
        label: type.name,
        id: type.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const getServiceRequestTagOptions = () => {
    if (!serviceRequestTags) return [];
    return serviceRequestTags.map((tag: ServiceRequestTag) => ({
      value: tag.id,
      label: tag.name,
    }));
  };

  const getIssueTypeOptions = () => {
    if (!issueTypes) return [];

    return issueTypes.map((issue: IssueType) => ({
      value: issue.id,
      label: issue.name,
    }));
  };

  const getServiceProviderOptions = () => {
    if (!serviceProviders) return [];

    return serviceProviders.reduce(
      (filtered: any[], contact: ServiceProvider) => {
        if (contact.role === 'Service Provider') {
          const newServiceProvider = {
            value: contact.id,
            label: contact.firstname + ' ' + contact.lastname,
          };

          filtered.push(newServiceProvider);
        }
        return filtered;
      },
      []
    );
  };

  return {
    assetTypeOptions: getAssetTypeOptions(),
    serviceRequestTagOptions: getServiceRequestTagOptions(),
    issueTypeOptions: getIssueTypeOptions(),
    serviceProviderOptions: getServiceProviderOptions(),
    loading:
      loadingAssetTypes ||
      loadingTags ||
      loadingIssueTypes ||
      loadingServiceProviders,
    error,
    fetchData,
  };
};

export default useFetchServiceRequestStatsFormData;
